import { defineStore, storeToRefs } from "pinia"
import { calculateDistanceBetweenCoordinates } from "@/helpers/geo"
import { useDatabaseStore } from "@/stores/database"
import { useSettingsStore } from "@/stores/settings"

// positionlogs up to this id were recorded without speed and altitude, the server delivers 0 for them
const LEGACY_MAX_POSITIONLOG_ID = 961196
const LEGACY_NO_DATA_COLUMNS = ["speed", "altitude"]
const ALL_COLUMNS = ["id", "tracker_id", "fix_at", "fix_timezone", "latitude", "longitude", "altitude", "speed", "course", "distance", "rssi", "sat", "battery", "voltage"]

// select list for positionlogs, speed and altitude of legacy positionlogs are returned as null ("no data") instead of 0.
// the WHERE conditions still see the stored values, sqlite resolves column names there before aliases
const selectColumns = (columns = null) => (columns ?? ALL_COLUMNS).map((column) => (LEGACY_NO_DATA_COLUMNS.includes(column) ? `CASE WHEN id <= ${LEGACY_MAX_POSITIONLOG_ID} THEN NULL ELSE ${column} END AS ${column}` : column)).join(", ")

export const useRouteStore = defineStore(
	"route",
	() => {
		const database = useDatabaseStore()
		const { useUnfilteredPositionlogs } = storeToRefs(useSettingsStore())

		const applyFilter = (trackerId = null) => {
			if (useUnfilteredPositionlogs.value) {
				return ""
			}

			let latestTrackerRecord = ""
			if (trackerId !== null) {
				latestTrackerRecord = `OR id = (SELECT max(id) FROM positionlogs WHERE tracker_id = ${trackerId})`
			}

			return `
				AND (
					speed IS NULL
					OR speed > 0
					${latestTrackerRecord}
					OR id <= ${LEGACY_MAX_POSITIONLOG_ID}
				)
				AND (
					distance IS NULL
					OR distance > 0
					${latestTrackerRecord}
					OR id <= ${LEGACY_MAX_POSITIONLOG_ID}
				)
			`
		}

		// columns: optional list of columns to select, keeps the payload over the native bridge small
		const getRoute = async (trackerId, routeFromTimestamp, routeToTimestamp, columns = null) => {
			const data = (
				await database.query(`
					SELECT ${selectColumns(columns)}
					FROM positionlogs
					WHERE tracker_id = ${trackerId}
					AND fix_at BETWEEN ${routeFromTimestamp} AND ${routeToTimestamp}
					${applyFilter(trackerId)}
					ORDER BY fix_at;`)
			).values

			return data
		}

		// columns: optional list of columns to select, keeps the payload over the native bridge small
		const getRawRoute = async (trackerId, routeFromTimestamp, routeToTimestamp, columns = null) => {
			const data = (
				await database.query(`
					SELECT ${selectColumns(columns)} 
					FROM positionlogs 
					WHERE tracker_id = ${trackerId} 
					AND fix_at BETWEEN ${routeFromTimestamp} AND ${routeToTimestamp}
					ORDER BY fix_at;`)
			).values

			return data
		}

		const getLineStringForRoute = async (trackerId, routeFromTimestamp, routeToTimestamp) => {
			const data = (
				await database.query(`
					SELECT id, latitude, longitude 
					FROM positionlogs 
					WHERE tracker_id = ${trackerId} 
					AND fix_at BETWEEN ${routeFromTimestamp} AND ${routeToTimestamp}
					${applyFilter(trackerId)}
					ORDER BY fix_at;`)
			).values

			return {
				lineString: data.map((positionlog) => [positionlog.longitude, positionlog.latitude]),
				ids: data.map((positionlog) => positionlog.id),
				lastPositionlogId: data[data.length - 1]?.id,
			}
		}

		const getPositionlogById = async (positionlogId) => {
			const data = await database.query(`SELECT ${selectColumns()} FROM positionlogs WHERE id = ${positionlogId};`)

			if (data.values.length > 0) {
				return data.values[0]
			} else {
				return false
			}
		}

		const getNextPositionlog = async (trackerId, positionlogId) => {
			const positionlog = await getPositionlogById(positionlogId)

			const data = await database.query(`
				SELECT ${selectColumns()} 
				FROM positionlogs 
				WHERE tracker_id = ${trackerId} 
				AND fix_at > '${positionlog.fix_at}'
				${applyFilter()}
				ORDER BY fix_at ASC 
				LIMIT 1;
			`)

			if (data.values.length > 0) {
				return data.values[0]
			} else {
				return false
			}
		}

		const getPreviousPositionlog = async (trackerId, positionlogId) => {
			const positionlog = await getPositionlogById(positionlogId)

			const data = await database.query(`
				SELECT ${selectColumns()} 
				FROM positionlogs 
				WHERE tracker_id = ${trackerId} 
				AND fix_at < '${positionlog.fix_at}'
				${applyFilter()}
				ORDER BY fix_at DESC 
				LIMIT 1;
			`)

			if (data.values.length > 0) {
				return data.values[0]
			} else {
				return false
			}
		}

		const getDataForCoordinate = async (latitude, longitude, trackers = null, routeFromTimestamp = null, routeToTimestamp = null) => {
			// extra conditions
			let trackersQueryCondition = trackers !== null ? `AND tracker_id IN (${trackers.join(",")})` : ""
			let routeFromQueryCondition = routeFromTimestamp !== null ? `AND fix_at >= ${routeFromTimestamp}` : ""
			let routeToQueryCondition = routeToTimestamp !== null ? `AND fix_at <= ${routeToTimestamp}` : ""

			let r = 0.000002

			let data
			do {
				r *= 2
				let range = {
					from: {
						latitude: latitude - r / 1.3333,
						longitude: longitude - r,
					},
					to: {
						latitude: latitude + r / 1.3333,
						longitude: longitude + r,
					},
				}

				data = await database.query(`
                    SELECT ${selectColumns()} FROM positionlogs 
                    WHERE latitude >= ${range.from.latitude} 
                    AND latitude <= ${range.to.latitude} 
                    AND longitude >= ${range.from.longitude} 
                    AND longitude <= ${range.to.longitude} 
                    ${applyFilter()}
                    ${trackersQueryCondition} 
                    ${routeFromQueryCondition} 
                    ${routeToQueryCondition};`)
			} while (data.values.length == 0 && r < 1)

			if (data.values.length > 1) {
				// if multiple records are found nearby, find closest
				let smallestDistance = null
				let closestRecord = null

				data.values.forEach((record) => {
					const distance = calculateDistanceBetweenCoordinates(latitude, longitude, record.latitude, record.longitude)

					if (smallestDistance === null || distance < smallestDistance || (distance == smallestDistance && record.fix_at > closestRecord.fix_at)) {
						smallestDistance = distance
						closestRecord = record
					}
				})

				return closestRecord
			} else if (data.values.length == 1) {
				return data.values[0]
			}

			return false
		}

		const getFirstPositionlog = async () => {
			const result = await database.query(`
				SELECT ${selectColumns()} FROM positionlogs WHERE id = (SELECT MIN(id) FROM positionlogs);
			`)
			return result.values[0]
		}

		const getFirstPositionlogForTracker = async (trackerId) => {
			const result = await database.query(`
				SELECT ${selectColumns()} FROM positionlogs WHERE id = (SELECT MIN(id) FROM positionlogs WHERE tracker_id = ${trackerId});
			`)
			return result.values[0]
		}

		const getLatestPositionlogForTracker = async (trackerId) => {
			const result = await database.query(`
				SELECT ${selectColumns()} FROM positionlogs WHERE id = (SELECT MAX(id) FROM positionlogs WHERE tracker_id = ${trackerId});
			`)
			return result.values[0]
		}

		// average voltage (in 1/100 V) of the latest positionlogs of the tracker, null without voltage data.
		// positionlogs without voltage (0) are skipped, they would pull the average down
		const getAverageVoltageForTracker = async (trackerId, count = 1000) => {
			const result = await database.query(`
				SELECT AVG(voltage) AS voltage FROM (
					SELECT voltage FROM positionlogs WHERE tracker_id = ${trackerId} AND voltage > 0 ORDER BY id DESC LIMIT ${count}
				);
			`)
			return result.values[0]?.voltage ?? null
		}

		const getTotalDistanceForAllTrackers = async () => {
			const result = await database.query(`
				SELECT SUM(distance) as distance FROM positionlogs;
			`)
			return result.values[0]
		}

		return {
			getRoute,
			getRawRoute,
			getLineStringForRoute,
			getDataForCoordinate,
			getPositionlogById,
			getFirstPositionlog,
			getFirstPositionlogForTracker,
			getLatestPositionlogForTracker,
			getNextPositionlog,
			getPreviousPositionlog,
			getAverageVoltageForTracker,
			getTotalDistanceForAllTrackers,
		}
	},
	{
		persist: false,
	},
)
